import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Select, Space, message, Modal,
} from 'antd';
import {
  SendOutlined, ReloadOutlined, MailOutlined, CheckCircleOutlined,
  EyeOutlined, TeamOutlined, WarningOutlined, LinkOutlined, CopyOutlined,
} from '@ant-design/icons';
import { tenderAPI, loiAPI } from '../services/api';

const { Option } = Select;

function TenderDashboardPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const res = await loiAPI.getCampaigns();
      setCampaigns(res.data);
      if (res.data.length > 0) {
        const saved = localStorage.getItem('spo_default_campaign_id');
        const savedId = saved ? Number(saved) : null;
        const match = res.data.find((c) => c.id === savedId);
        setSelectedCampaign(match ? match.id : res.data[0].id);
      }
    } catch (err) {
      message.error('載入活動清單失敗');
    }
  };

  const loadDashboard = useCallback(async () => {
    if (!selectedCampaign) return;
    setLoading(true);
    try {
      const [statsRes, dealersRes] = await Promise.all([
        tenderAPI.getDashboardStats(selectedCampaign),
        tenderAPI.getDealerStatus(selectedCampaign),
      ]);
      setStats(statsRes.data);
      setDealers(dealersRes.data);
    } catch (err) {
      message.error('載入儀表板失敗');
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleSendEmails = async (dealerIds = null) => {
    Modal.confirm({
      title: dealerIds ? '發送招標通知' : '批次發送招標通知',
      content: dealerIds
        ? '確定要對選定的經銷商發送招標通知信？'
        : '確定要對所有經銷商發送招標通知信？',
      okText: '確定發送',
      cancelText: '取消',
      onOk: async () => {
        setSending(true);
        try {
          const res = await tenderAPI.sendTenderEmails(selectedCampaign, {
            dealer_ids: dealerIds,
            email_type: 'tender_notify',
          });
          message.success(`發送完成：成功 ${res.data.success} / 失敗 ${res.data.failed}`);
          loadDashboard();
        } catch (err) {
          message.error('發送失敗：' + (err.message || '未知錯誤'));
        } finally {
          setSending(false);
        }
      },
    });
  };

  const handleSendReminders = async () => {
    Modal.confirm({
      title: '一鍵催標',
      content: '確定要對所有「未確認報價」的經銷商重發催標通知？',
      okText: '確定催標',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setSending(true);
        try {
          const res = await tenderAPI.sendTenderReminders(selectedCampaign);
          message.success(`催標完成：成功 ${res.data.success} / 失敗 ${res.data.failed}`);
          loadDashboard();
        } catch (err) {
          message.error('催標失敗：' + (err.message || '未知錯誤'));
        } finally {
          setSending(false);
        }
      },
    });
  };

  const statusColor = {
    '未發送': 'default',
    '已發送未讀': 'orange',
    '已讀未確認': 'blue',
    '已確認領標': 'cyan',
    '已確認報價': 'green',
  };

  const columns = [
    {
      title: '經銷商',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 200,
    },
    {
      title: '聯絡人',
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: 100,
    },
    {
      title: 'Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: 200,
    },
    {
      title: '電話',
      key: 'phone',
      width: 150,
      render: (_, record) => record.contact_mobile || record.contact_phone || '-',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={statusColor[status] || 'default'}>{status}</Tag>
      ),
      filters: Object.keys(statusColor).map((s) => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '公開連結',
      key: 'links',
      width: 160,
      render: (_, record) => {
        if (!record.token) return '-';
        const base = window.location.origin;
        return (
          <Space size={4}>
            <Button
              size="small"
              type="link"
              icon={<LinkOutlined />}
              onClick={() => window.open(`${base}/survey/${record.token}`, '_blank')}
              title="開啟意向書"
            >
              意向書
            </Button>
            <Button
              size="small"
              type="link"
              icon={<LinkOutlined />}
              onClick={() => window.open(`${base}/prices/${record.token}`, '_blank')}
              title="開啟價格清單"
            >
              價格
            </Button>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              title="複製連結"
              onClick={() => {
                const links = `意向書: ${base}/survey/${record.token}\n價格清單: ${base}/prices/${record.token}`;
                navigator.clipboard.writeText(links).then(
                  () => message.success(`已複製 ${record.company_name} 的連結`),
                  () => message.error('複製失敗')
                );
              }}
            />
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        record.status === '未發送' || record.status === '已發送未讀' ? (
          <Button
            size="small"
            icon={<MailOutlined />}
            onClick={() => handleSendEmails([record.id])}
            loading={sending}
          >
            {record.status === '未發送' ? '發送' : '催標'}
          </Button>
        ) : null
      ),
    },
  ];

  const unconfirmedCount = dealers.filter(
    (d) => d.status !== '已確認報價'
  ).length;

  return (
    <div>
      <h2>招標填報監控後台</h2>

      {/* Campaign Selector */}
      <Space style={{ marginBottom: 20 }}>
        <span>選擇活動：</span>
        <Select
          value={selectedCampaign}
          onChange={setSelectedCampaign}
          style={{ width: 400 }}
          placeholder="選擇活動"
        >
          {campaigns.map((c) => (
            <Option key={c.id} value={c.id}>
              {c.case_number} - {c.title}
            </Option>
          ))}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={loadDashboard} loading={loading}>
          重新整理
        </Button>
      </Space>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="總經銷商"
                value={stats.total_dealers}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已發信"
                value={stats.total_sent}
                prefix={<SendOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已點擊頁面"
                value={stats.total_page_viewed}
                prefix={<EyeOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已確認領標"
                value={stats.total_confirmed_tender}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已確認報價"
                value={stats.total_confirmed_price}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="未確認"
                value={unconfirmedCount}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Action Buttons */}
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={() => handleSendEmails()}
          loading={sending}
          size="large"
        >
          批次發送招標通知
        </Button>
        <Button
          danger
          icon={<WarningOutlined />}
          onClick={handleSendReminders}
          loading={sending}
          size="large"
        >
          一鍵催標（未確認者）
        </Button>
      </Space>

      {/* Dealer Status Table */}
      <Card title="經銷商領標狀態">
        <Table
          columns={columns}
          dataSource={dealers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 900 }}
          summary={() => {
            if (!dealers.length) return null;
            const statusCounts = {};
            dealers.forEach((d) => {
              statusCounts[d.status] = (statusCounts[d.status] || 0) + 1;
            });
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5}>
                  <Space>
                    {Object.entries(statusCounts).map(([status, count]) => (
                      <Tag key={status} color={statusColor[status]}>
                        {status}: {count}
                      </Tag>
                    ))}
                  </Space>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </div>
  );
}

export default TenderDashboardPage;
